import { useCallback, useEffect, useState } from 'react'
import { api } from '../api/client'
import { orario, syncAndWait, syncPortaliApi, type SyncPortaleStato } from '../api/syncPortali'
import { Button, Callout, Card, CardContent, CardHeader, DataTable, StatusPill } from './ui'
import { SyncAllPortalsButton } from './SyncAllPortalsButton'

/**
 * Pausa della sincronizzazione con il portale di un gestore (`/api/sync-pausa`): ad esempio per entrare su A2A,
 * che ammette una sola sessione per utente, senza che il CRM ne apra un'altra. Gli script saltano i giri fino
 * alla fine della pausa.
 */
interface SyncPauseItem { gestore: string; pausaFino: string | null }

const NOMI: Record<string, string> = { a2a: 'A2A', ermes: 'Ermes', teamwork: 'Teamwork', coraly: 'Coraly' }
const ora = (iso: string) => new Date(iso).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })

export function SyncPauseCard() {
  const [items, setItems] = useState<SyncPauseItem[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [sync, setSync] = useState<Record<string, SyncPortaleStato>>({})

  const load = useCallback(async () => {
    try {
      const [pause, stati] = await Promise.all([api<SyncPauseItem[]>('/api/sync-pausa'), syncPortaliApi.list()])
      setItems(pause)
      setSync(Object.fromEntries(stati.map(s => [s.gestore, s])))
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore nel caricamento')
    }
  }, [])
  useEffect(() => { void load() }, [load])

  async function run(gestore: string, minuti: number | null) {
    setBusy(gestore)
    try {
      await api(`/api/sync-pausa/${gestore}`, minuti === null
        ? { method: 'DELETE' }
        : { method: 'POST', body: JSON.stringify({ minuti }) })
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore nel salvataggio')
    } finally {
      setBusy(null)
    }
  }

  async function syncNow(gestore: string) {
    setBusy(gestore)
    try {
      await syncAndWait(gestore, undefined, s => setSync(prev => ({ ...prev, [gestore]: s })))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Sync non riuscito')
    } finally {
      setBusy(null)
    }
  }

  // Log dello script Ermes sul server (solo amministratori): conteggi e nomi di campi, per capire un giro senza entrare nel server.
  const [log, setLog] = useState<{ file: string; righe: string[]; aggiornato?: string; nota?: string } | null>(null)
  const [logMsg, setLogMsg] = useState<string | null>(null)
  async function showLog() {
    setBusy('log')
    try {
      setLog(await syncPortaliApi.log('ermes', 120))
      setLogMsg(null)
    } catch (e) {
      setLogMsg(e instanceof Error ? e.message : 'Log non leggibile')
    } finally {
      setBusy(null)
    }
  }
  async function esplora() {
    setBusy('log')
    try {
      const r = await syncPortaliApi.esploraErmes()
      setLogMsg(r.nota)
    } catch (e) {
      setLogMsg(e instanceof Error ? e.message : 'Richiesta non riuscita')
    } finally {
      setBusy(null)
    }
  }

  return (
    <Card>
      <CardHeader title="Sincronizzazione portali" actions={<SyncAllPortalsButton compact onDone={() => { void load() }} />} />
      <CardContent>
        <p className="md-typescale-body-small">
          Il CRM legge stati e pratiche dai portali ogni 10 minuti; «Sync ora» li legge subito. Metti in pausa la lettura
          di un portale per accedervi tu senza che il CRM apra un'altra sessione (A2A ammette un solo accesso per
          utente): alla fine della pausa riprende da sola.
        </p>
        {error && <Callout tone="error">{error}</Callout>}
        {items && (
          <DataTable variant="plain" aria-label="Sincronizzazione portali">
            <thead><tr><th>Portale</th><th>Stato</th><th>Ultimo sync</th><th /></tr></thead>
            <tbody>
              {items.map(i => (
                <tr key={i.gestore}>
                  <td>{NOMI[i.gestore] ?? i.gestore}</td>
                  <td>
                    {i.pausaFino
                      ? <StatusPill tone="warning">In pausa fino alle {ora(i.pausaFino)}</StatusPill>
                      : <StatusPill tone="success">Attiva</StatusPill>}
                  </td>
                  <td>
                    {sync[i.gestore]
                      ? sync[i.gestore]!.inCorso ? 'in corso…' : `${orario(sync[i.gestore]!.eseguitaIl)}${sync[i.gestore]!.esito && sync[i.gestore]!.esito !== 'ok' ? ` (${sync[i.gestore]!.esito})` : ''}`
                      : i.gestore === 'coraly' ? 'dalla pratica' : '—'}
                  </td>
                  <td>
                    {sync[i.gestore] && (
                      <Button type="button" variant="text" size="sm" disabled={busy === i.gestore || sync[i.gestore]!.inCorso} onClick={() => { void syncNow(i.gestore) }}>
                        Sync ora
                      </Button>
                    )}
                    <Button type="button" variant="text" size="sm" disabled={busy === i.gestore} onClick={() => { void run(i.gestore, 60) }}>
                      {i.pausaFino ? 'Prolunga 1 ora' : 'Pausa 1 ora'}
                    </Button>
                    {i.pausaFino && (
                      <Button type="button" variant="text" size="sm" disabled={busy === i.gestore} onClick={() => { void run(i.gestore, null) }}>
                        Riprendi ora
                      </Button>
                    )}
                    {i.gestore === 'ermes' && (
                      <>
                        <Button type="button" variant="text" size="sm" disabled={busy === 'log'} onClick={() => { void showLog() }}>Log</Button>
                        <Button type="button" variant="text" size="sm" disabled={busy === 'log'} onClick={() => { void esplora() }}>Esplora</Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        )}
        {logMsg && <Callout tone="info">{logMsg}</Callout>}
        {log && (
          <div className="mt-2">
            <div className="d-flex align-items-center justify-content-between">
              <span className="md-typescale-body-small text-muted">
                {log.file}{log.aggiornato ? ` · aggiornato ${orario(log.aggiornato)}` : ''}{log.nota ? ` · ${log.nota}` : ''} · solo conteggi e nomi di campi
              </span>
              <Button type="button" variant="text" size="sm" onClick={() => setLog(null)}>Chiudi</Button>
            </div>
            <pre className="md-typescale-body-small" style={{ maxHeight: 360, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {log.righe.join('\n') || '(vuoto)'}
            </pre>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
